export interface CreateTracking {
  event: TrackingEventsType;
  data: Partial<TrackingData>;
}

export interface TrackingData {
  userId: string | 0;
  timestamp: string | null;
  email: string | null;
  age: string | null;
  gender: string | null;
  campaign: string | null;
  userType: UserType | null;
  landingPage: string | null;
  deviceType: DeviceType | null;
  dailyTrackingScore: string | null;
  category: CategoryType | null;
  resourceId: string | null;
  resourceType: ResourceType | null;
  timeSpent: string | null;
  rating: string | null;
  supporterId: string | null;
  durationSec: string | null;
  ratingValue: string | null;
  comment: string | null;
  profileData: string | null;
  selectedSpeciality: string | null;
  selectedReason: string[] | null;
  selectedType: string | null;
  selectedCity: string | null;
  centerId: string | null;
  centerName: string | null;
  dataOptions: string | null;
  psychologistId: string | null;
  psychologistName: string | null;
  recommendedFlow: boolean | null;
  speciality: string | null;
  consultationType: string | null;
  dateTime: string | null;
  consultationReasons: string | null;
  city: string | null;
  name: string | null;
  date: string | null;
  // CHAT
  sessionId: string | null;
  senderId: string | null;
  receiverId: string | null;
  messageType: ChatMessageType | null;
  updateType: ChatUpdateType | null;
  messageId: string | null;
  pinnedBy: ChatUserType | null;
  fileType: ChatFileType | string | null;
  // SURVEY
  question: string | null;
  surveyType: string | null;
  answer: string | null;
  score: string | null;
  requiredTextValue: string | null;
  surveyName: string | null;
  surveyScorePoints: string | null;
  surveyScorePercentage: string | null;
  emotionalScorePoints: string | null;
  emotionalScorePercentage: string | null;
  relationalScorePoints: string | null;
  relationalScorePercentage: string | null;
  professionalScorePoints: string | null;
  professionalScorePercentage: string | null;
  healthScorePoints: string | null;
  healthScorePercentage: string | null;
  // SAPA
  sapaReportTimestamp: string | null;
  sapaUserData: Record<string, unknown> | null;
  sapaAnswers: Record<string, unknown> | null;
}

export enum TrackingEventsType {
  // USER
  USER_ENTERS = 'User enters on Site',
  USER_SIGN_UP_STARTED = 'User Sign Up Started',
  USER_SIGN_UP_COMPLETED = 'User Sign Up Completed',
  USER_LOGIN_COMPLETED = 'User Login Completed',
  USER_RESET_PASSWORD = 'User Reset Password Request',
  // ENGAGEMENT
  DAILY_PULSE_COMPLETED = 'Daily Pulse Completed',
  SURVEY_QUESTION_ANSWERED = 'Survey Question Answered',
  SURVEY_COMPLETED = 'Survey completed',
  USER_OPENS_RESOURCE = 'User Opens Resource',
  USER_COMPLETES_RESOURCE = 'User Completes Resource',
  USER_STARTS_EMOTIONAL_SUPPORT_CHAT = 'User Starts Emotional Support Chat',
  USER_EXIT_EMOTIONAL_SUPPORT_CHAT = 'User Exits Emotional Support Chat',
  USER_RATES_EMOTIONAL_SUPPORT_CHAT = 'User Rates Emotional Support Chat',
  NPS_QUESTION_ANSWERED = 'NPS Question Answered',
  // APPOINTMENTS
  USER_OPEN_APPOINTMENT_BOOKING_PAGE = 'User Opens Appointment Booking Page',
  USER_CONFIGURES_PROFILE = 'User Configures Profile',
  USER_SELECTS_SPECIALITY = 'User Selects Speciality',
  USER_SELECTS_CONSULTATION_REASON = 'User Selects Consultation Reason',
  USER_SELECTS_CONSULTATION_TYPE = 'User Selects Consultation Type',
  USER_SELECTS_CITY = ' User Selects City',
  USER_SELECTS_CENTER = 'User Selects Center',
  USER_SELECTS_PREFERRED_SCHEDULE = 'User Selects Preferred Schedule',
  USER_SELECTS_PROFESSIONAL = 'User Selects Professional',
  USER_CONFIRMS_APPOINTMENT = 'User Confirms the appointment',
  USER_ATTENDS_TRAINING_SESSION = 'User attends a training session',
  THERAPY_SESSION_RATED = 'Therapy session rated',
  // CHAT
  CHAT_MESSAGE_SENT = ' Chat Message Sent',
  CHAT_MESSAGE_UPDATED = 'Chat Message Updated',
  CHAT_MESSAGE_PINNED = 'Chat Message Pinned',
  CHAT_FILE_SHARED = 'Chat File Shared',
  CHAT_AUDIO_NOTE_RECORDED = 'Chat Audio Note Recorded',
  // SAPA
  SAPA_REPORT_SUBMITTED = 'SAPA Report Submitted',
}

export enum UserType {
  PATIENT = 'patient',
  SUPPORTER = 'supporter',
  AGENT = 'agent',
  PSYCHOLOGIST = 'psychologist',
}

export enum CategoryType {
  MENTAL_HEALTH = 'mentalHealth',
  HEALTHY_HABITS = 'healthyHabits',
  COMPETENCIES = 'competencies',
  RELATIONAL_HEALTH = 'relationalHealth',
  PROFESSIONAL_FULFILLMENT = 'professionalFulfillment',
  OTHER = 'other',
}

export const UserTypeTranslationMap: Record<string, UserType> = {
  PatientDetailDTO: UserType.PATIENT,
  SupporterDetailDTO: UserType.SUPPORTER,
  AgentDetailDTO: UserType.AGENT,
  PsychologistDetailDTO: UserType.PSYCHOLOGIST,
};

export const categoryTranslationMap: Record<string, CategoryType> = {
  'salud mental': CategoryType.MENTAL_HEALTH,
  habitos: CategoryType.HEALTHY_HABITS,
  competencias: CategoryType.COMPETENCIES,
  otro: CategoryType.OTHER,
};

const BAROMETER_TAGS = {
  SALUD_EMOCIONAL: 'salud-emocional',
  SALUD_RELACIONAL: 'salud-relacional',
  HABITOS_DE_SALUD: 'habitos-de-salud',
  REALIZACION_PROFESIONAL: 'realizacion-profesional',
} as const;

export const barometerMap: Record<string, CategoryType> = {
  [BAROMETER_TAGS.SALUD_EMOCIONAL]: CategoryType.MENTAL_HEALTH,
  [BAROMETER_TAGS.SALUD_RELACIONAL]: CategoryType.RELATIONAL_HEALTH,
  [BAROMETER_TAGS.HABITOS_DE_SALUD]: CategoryType.HEALTHY_HABITS,
  [BAROMETER_TAGS.REALIZACION_PROFESIONAL]: CategoryType.PROFESSIONAL_FULFILLMENT,
};

export enum DeviceType {
  DESKTOP = 'desktop',
  MOBILE = 'mobile',
}

export enum ResourceType {
  TIPS = 'tips',
  EBOOK = 'ebook',
  EXERCISE = 'exercise',
  TRAINING = 'training',
  ARTICLE = 'article',
  PODCAST = 'podcast',
}

// CHAT
export enum ChatMessageType {
  TEXT = 'texto',
  IMAGE = 'imagen',
  AUDIO = 'audio',
  FILE = 'file',
}

export enum ChatUpdateType {
  EDITED = 'edited',
  DELETED = 'deleted',
}

export enum ChatUserType {
  USER = 'userId',
  PROFESSIONAL = 'professionalId',
}

enum ChatFileType {
  PDF = 'pdf',
  DOC = 'doc',
  IMAGE = 'image',
}
